import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '../components/ui/accordion';
import {
  Users,
  ArrowLeft,
  LogIn,
  ScanFace,
  Clock,
  History,
  ClipboardList,
  ScanLine,
  UserCircle,
  Smartphone,
  UserCog,
  Building2,
  MapPin,
  CalendarClock,
  ShieldCheck,
  FileClock,
  Info,
} from 'lucide-react';

const Step = ({ n, title, children }) => (
  <div className="flex gap-3 items-start">
    <div className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">
      {n}
    </div>
    <div>
      <p className="font-medium text-sm">{title}</p>
      {children && <p className="text-sm text-muted-foreground mt-0.5">{children}</p>}
    </div>
  </div>
);

const roleMatrix = [
  { menu: 'Absensi, Riwayat Absensi, Registrasi Wajah, Laporan Kerja, Patroli', roles: 'Semua role (karyawan yang terhubung ke data karyawan)' },
  { menu: 'Karyawan (lihat)', roles: 'Super Admin, HR, Manager' },
  { menu: 'Karyawan (tambah/edit/hapus)', roles: 'Super Admin, HR' },
  { menu: 'Departemen, Posisi, Site, Manajemen Shift, Pos Keamanan, Kontrak', roles: 'Super Admin, HR' },
  { menu: 'User Management (lihat daftar user)', roles: 'Super Admin, HR' },
  { menu: 'User Management (ubah role)', roles: 'Super Admin saja' },
];

const PanduanPage = () => {
  const [tab, setTab] = useState('user');

  return (
    <div className="min-h-screen bg-slate-50" data-testid="panduan-page">
      <header className="h-16 bg-white border-b border-border flex items-center justify-between px-4 lg:px-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-primary rounded-lg flex items-center justify-center">
            <img src="/logo192.png" alt="SILSA" className="w-full h-full object-cover" />
          </div>
          <span className="text-xl font-bold tracking-tight font-['Manrope']">SILSA</span>
        </div>
        <Link
          to="/dashboard"
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
          data-testid="panduan-back-link"
        >
          <ArrowLeft className="w-4 h-4" /> Kembali ke Dashboard
        </Link>
      </header>

      <main className="max-w-4xl mx-auto p-4 lg:p-8 space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-['Manrope'] tracking-tight">
            Panduan Penggunaan SILSA
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Panduan lengkap untuk Karyawan (User) dan Admin/HR dalam menggunakan sistem SILSA.
          </p>
        </div>

        <Tabs value={tab} onValueChange={setTab} data-testid="panduan-tabs">
          <TabsList className="grid w-full grid-cols-2 h-auto">
            <TabsTrigger value="user" data-testid="tab-user" className="py-2">
              Untuk Karyawan (User)
            </TabsTrigger>
            <TabsTrigger value="admin" data-testid="tab-admin" className="py-2">
              Untuk Admin / HR
            </TabsTrigger>
          </TabsList>

          {/* ============ USER TAB ============ */}
          <TabsContent value="user" className="mt-6">
            <Card>
              <CardContent className="p-2 sm:p-4">
                <Accordion type="multiple" defaultValue={['login', 'absen']} data-testid="accordion-user">

                  <AccordionItem value="login" data-testid="acc-login">
                    <AccordionTrigger className="px-2">
                      <span className="flex items-center gap-2"><LogIn className="w-4 h-4 text-primary" />1. Cara Login & Ganti Password</span>
                    </AccordionTrigger>
                    <AccordionContent className="px-2 space-y-3">
                      <Step n="1" title="Buka hr.silsa.id atau aplikasi SILSA di HP Anda" />
                      <Step n="2" title="Masukkan Nomor Telepon dan Password yang diberikan HR" />
                      <Step n="3" title='Lupa/belum punya password? Klik "Lupa/belum punya password? Minta lewat WhatsApp" di halaman login'>
                        Nomor WhatsApp Anda harus sudah terdaftar di sistem oleh HR. Tap tombol untuk membuka WhatsApp, kirim pesan yang sudah otomatis terisi, dan password akan dibalas otomatis dalam beberapa detik.
                      </Step>
                      <Step n="4" title="Login pertama kali dengan password sementara">
                        Anda akan diminta membuat password baru 6 digit angka di halaman "Ganti Password". Password ini yang akan Anda pakai untuk login selanjutnya.
                      </Step>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="face" data-testid="acc-face">
                    <AccordionTrigger className="px-2">
                      <span className="flex items-center gap-2"><ScanFace className="w-4 h-4 text-primary" />2. Registrasi Wajah (Wajib Sebelum Absen)</span>
                    </AccordionTrigger>
                    <AccordionContent className="px-2 space-y-3">
                      <p className="text-sm text-muted-foreground">
                        Buka menu <strong>Registrasi Wajah</strong>. Wajah Anda WAJIB terdaftar sebelum tombol Clock In/Clock Out di menu Absensi bisa dipakai.
                      </p>
                      <Step n="1" title='Klik "Buka Kamera" dan posisikan wajah di tengah frame' />
                      <Step n="2" title='Tunggu badge hijau "Wajah Terdeteksi", lalu klik "Ambil"' />
                      <Step n="3" title='Periksa hasil foto, klik "Simpan Wajah"' />
                      <Step n="4" title='Jika verifikasi wajah sering gagal saat absen, buka menu ini lagi dan klik "Daftar Ulang"' />
                      <div className="bg-blue-50 rounded-lg p-3 text-xs text-blue-800">
                        Tips: pastikan pencahayaan cukup, wajah menghadap kamera, dan lepas aksesoris (masker/kacamata gelap) saat mendaftar.
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="absen" data-testid="acc-absen">
                    <AccordionTrigger className="px-2">
                      <span className="flex items-center gap-2"><Clock className="w-4 h-4 text-primary" />3. Absensi (Clock In / Clock Out)</span>
                    </AccordionTrigger>
                    <AccordionContent className="px-2 space-y-3">
                      <Step n="1" title='Buka menu Absensi, klik "Clock In" saat masuk kerja atau "Clock Out" saat pulang' />
                      <Step n="2" title="Pilih Mode Kerja">
                        Work From Office (WFO - lokasi HP harus berada dalam radius kantor/site yang ditentukan HR), Work From Home (WFH), atau Client Visit (wajib isi alamat client).
                      </Step>
                      <Step n="3" title="Pilih Shift kerja hari ini (saat Clock In)">
                        Kalau perusahaan sudah mengatur shift, pilih shift yang sesuai. Saat Clock Out, sistem otomatis memakai shift yang sama dengan Clock In - tidak perlu pilih ulang.
                      </Step>
                      <Step n="4" title='Buka kamera, sistem akan memverifikasi wajah Anda otomatis (dicocokkan dengan data terdaftar) lalu ambil foto' />
                      <Step n="5" title="Lokasi GPS diambil otomatis" />
                      <Step n="6" title='Klik tombol "Clock In"/"Clock Out" untuk mengirim absensi' />
                      <div className="bg-amber-50 rounded-lg p-3 text-xs text-amber-800">
                        Status <strong>"Terlambat"</strong> otomatis muncul jika Clock In melewati jam masuk shift + toleransi yang diatur HR. Untuk karyawan shift malam yang jam kerjanya melewati tengah malam, Clock Out tetap bisa dilakukan meski tanggal kalender sudah berganti.
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="riwayat" data-testid="acc-riwayat">
                    <AccordionTrigger className="px-2">
                      <span className="flex items-center gap-2"><History className="w-4 h-4 text-primary" />4. Riwayat Absensi</span>
                    </AccordionTrigger>
                    <AccordionContent className="px-2 space-y-2 text-sm text-muted-foreground">
                      <p>Menu <strong>Riwayat Absensi</strong> menampilkan statistik bulanan: jumlah hari kerja, hadir, terlambat, alpha, dan persentase kehadiran.</p>
                      <p>Gunakan filter bulan untuk melihat periode lain. Klik ikon mata pada tiap baris untuk melihat detail (foto Clock In/Out dan koordinat lokasi saat absen).</p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="laporan" data-testid="acc-laporan">
                    <AccordionTrigger className="px-2">
                      <span className="flex items-center gap-2"><ClipboardList className="w-4 h-4 text-primary" />5. Laporan Kerja</span>
                    </AccordionTrigger>
                    <AccordionContent className="px-2 space-y-2 text-sm text-muted-foreground">
                      <p>Menu <strong>Laporan Kerja</strong> untuk mengirim bukti kerja berupa foto yang otomatis diberi stempel tanggal, jam, dan koordinat lokasi.</p>
                      <p>Ambil foto lewat kamera, isi kolom deskripsi pekerjaan yang dilakukan, lalu klik "Kirim Laporan". Riwayat laporan Anda sendiri tampil di bagian bawah halaman.</p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="patroli" data-testid="acc-patroli">
                    <AccordionTrigger className="px-2">
                      <span className="flex items-center gap-2"><ScanLine className="w-4 h-4 text-primary" />6. Patroli Keamanan (Khusus Satpam)</span>
                    </AccordionTrigger>
                    <AccordionContent className="px-2 space-y-3">
                      <Step n="1" title='Buka menu Patroli Keamanan, klik "Scan QR Pos" lalu arahkan kamera ke QR code di titik pos, atau masukkan kode pos secara manual' />
                      <Step n="2" title="Setelah pos ditemukan, ambil 4 foto: Depan, Belakang, Kanan, Kiri secara berurutan">
                        Setiap foto otomatis diberi stempel tanggal, jam, dan koordinat lokasi.
                      </Step>
                      <Step n="3" title='Setelah 4 foto lengkap, klik "Kirim Laporan Patroli"' />
                      <p className="text-xs text-muted-foreground">Riwayat ronda Anda sendiri tampil di bagian bawah halaman.</p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="profil" data-testid="acc-profil">
                    <AccordionTrigger className="px-2">
                      <span className="flex items-center gap-2"><UserCircle className="w-4 h-4 text-primary" />7. Profil Saya</span>
                    </AccordionTrigger>
                    <AccordionContent className="px-2 text-sm text-muted-foreground">
                      Menu <strong>Profil Saya</strong> menampilkan data akun dan data karyawan Anda. Anda bisa memperbarui nomor telepon dan alamat sendiri di sini.
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="install" data-testid="acc-install">
                    <AccordionTrigger className="px-2">
                      <span className="flex items-center gap-2"><Smartphone className="w-4 h-4 text-primary" />8. Install Aplikasi di HP</span>
                    </AccordionTrigger>
                    <AccordionContent className="px-2 space-y-2 text-sm text-muted-foreground">
                      <p><strong>Android:</strong> di halaman Login, klik tombol "Android APK" untuk download dan install file APK.</p>
                      <p><strong>iPhone/iPad:</strong> klik tombol "iOS PWA" di halaman Login untuk melihat langkah instalasi (buka di Safari → tombol Share → "Add to Home Screen"). Setelah terinstall, SILSA bisa dibuka langsung dari home screen seperti aplikasi biasa.</p>
                    </AccordionContent>
                  </AccordionItem>

                </Accordion>
              </CardContent>
            </Card>
            <div className="mt-4 flex items-start gap-2 text-xs text-muted-foreground bg-slate-100 rounded-lg p-3">
              <Info className="w-4 h-4 shrink-0 mt-0.5" />
              <p>Menu Pengajuan Cuti, Lembur, dan Kalender sudah dibangun di sistem namun untuk saat ini belum ditampilkan di menu utama.</p>
            </div>
          </TabsContent>

          {/* ============ ADMIN/HR TAB ============ */}
          <TabsContent value="admin" className="mt-6">
            <Card>
              <CardContent className="p-2 sm:p-4">
                <Accordion type="multiple" defaultValue={['karyawan']} data-testid="accordion-admin">

                  <AccordionItem value="karyawan" data-testid="acc-karyawan">
                    <AccordionTrigger className="px-2">
                      <span className="flex items-center gap-2"><Users className="w-4 h-4 text-primary" />1. Manajemen Karyawan</span>
                    </AccordionTrigger>
                    <AccordionContent className="px-2 space-y-3">
                      <Step n="1" title='Buka menu Karyawan, klik "Tambah Karyawan"' />
                      <Step n="2" title="Isi data lengkap: NIK, nama, email, telepon, departemen, posisi, site (lokasi kerja), tanggal bergabung, dan data kontrak" />
                      <Step n="3" title='Klik "Tambah Karyawan" untuk menyimpan'>
                        Akun login dibuat otomatis dengan password 6 digit yang di-generate sistem. Kredensial (nomor HP login + password) langsung ditampilkan - klik "Salin" atau "Kirim via WA" untuk membagikan ke karyawan.
                      </Step>
                      <Step n="4" title="Gunakan kolom pencarian dan filter (Departemen/Site/Status) untuk menemukan karyawan" />
                      <Step n="5" title='Klik ikon pensil untuk edit, ikon tempat sampah untuk hapus (data karyawan yang masih terkait shift/site tetap bisa dihapus, tapi site/departemen/posisi yang masih dipakai karyawan tidak bisa dihapus)' />
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="struktur" data-testid="acc-struktur">
                    <AccordionTrigger className="px-2">
                      <span className="flex items-center gap-2"><Building2 className="w-4 h-4 text-primary" />2. Departemen & Posisi/Jabatan</span>
                    </AccordionTrigger>
                    <AccordionContent className="px-2 space-y-2 text-sm text-muted-foreground">
                      <p><strong>Departemen:</strong> menu Departemen untuk membuat kode & nama departemen (contoh: IT, HR, FIN). Departemen yang masih memiliki karyawan tidak bisa dihapus.</p>
                      <p><strong>Posisi/Jabatan:</strong> menu Posisi untuk membuat jabatan, dikaitkan ke satu departemen, dengan level (1-Staff s/d 5-C-Level) yang menentukan urutan hierarki.</p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="site" data-testid="acc-site">
                    <AccordionTrigger className="px-2">
                      <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-primary" />3. Site / Lokasi Kerja (Geofence Absensi)</span>
                    </AccordionTrigger>
                    <AccordionContent className="px-2 space-y-3">
                      <p className="text-sm text-muted-foreground">Site menentukan area (geofence) yang dipakai untuk validasi absensi mode WFO. Setiap karyawan bisa di-assign ke satu Site.</p>
                      <Step n="1" title='Buka menu Site, klik "Tambah Site"' />
                      <Step n="2" title="Isi nama & alamat lokasi" />
                      <Step n="3" title='Isi koordinat Latitude/Longitude manual, atau klik "Gunakan Lokasi Saat Ini" saat berada di lokasi tersebut' />
                      <Step n="4" title="Tentukan Radius Geofence dalam meter (default 100m)">
                        Karyawan absen WFO harus berada dalam radius ini dari titik koordinat Site, kalau tidak, absensi ditolak dengan pesan jarak dalam meter.
                      </Step>
                      <Step n="5" title="Assign Site ke karyawan lewat form Tambah/Edit Karyawan" />
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="shift" data-testid="acc-shift">
                    <AccordionTrigger className="px-2">
                      <span className="flex items-center gap-2"><CalendarClock className="w-4 h-4 text-primary" />4. Manajemen Shift</span>
                    </AccordionTrigger>
                    <AccordionContent className="px-2 space-y-3">
                      <Step n="1" title='Klik "Buat Shift": isi nama shift, jam masuk, jam keluar, dan warna label'>
                        Shift bisa lintas hari (contoh Shift 2: 20:00 - 08:00) untuk shift malam, sistem sudah menangani absensi yang melewati tengah malam.
                      </Step>
                      <Step n="2" title='Klik "Assign Shift" untuk menugaskan shift ke karyawan tertentu, dengan tanggal mulai (wajib) dan tanggal selesai (opsional, kosongkan jika berlaku terus)' />
                      <Step n="3" title="Filter tabel Jadwal Shift Karyawan berdasarkan departemen" />
                      <p className="text-xs text-muted-foreground">Karyawan memilih shift ini sendiri saat Clock In dari menu Absensi.</p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="pos" data-testid="acc-pos">
                    <AccordionTrigger className="px-2">
                      <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-primary" />5. Pos Keamanan & QR Code Patroli</span>
                    </AccordionTrigger>
                    <AccordionContent className="px-2 space-y-3">
                      <Step n="1" title='Buka menu Pos Keamanan, klik "Tambah Pos"' />
                      <Step n="2" title="Lokasi bersifat opsional">
                        Klik "Gunakan Lokasi Saat Ini" hanya jika Anda sedang berada di titik pos tersebut dan ingin koordinatnya ikut tercatat. Tanpa koordinat pun QR code tetap dibuat dan bisa langsung dicetak.
                      </Step>
                      <Step n="3" title="Simpan - sistem otomatis membuat kode pos unik dan QR code" />
                      <Step n="4" title="Cetak/tempel QR code di titik pos fisik">
                        Petugas satpam akan scan QR ini setiap ronda lewat menu Patroli Keamanan.
                      </Step>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="kontrak" data-testid="acc-kontrak">
                    <AccordionTrigger className="px-2">
                      <span className="flex items-center gap-2"><FileClock className="w-4 h-4 text-primary" />6. Kontrak Karyawan</span>
                    </AccordionTrigger>
                    <AccordionContent className="px-2 text-sm text-muted-foreground">
                      Menu <strong>Kontrak</strong> otomatis menampilkan daftar karyawan aktif dengan tanggal berakhir kontrak dalam 30 hari ke depan (termasuk yang sudah lewat/terlambat perpanjangan), lengkap dengan badge peringatan warna (hijau/kuning/merah). Data tanggal kontrak diisi lewat form Tambah/Edit Karyawan.
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="user" data-testid="acc-user-mgmt">
                    <AccordionTrigger className="px-2">
                      <span className="flex items-center gap-2"><UserCog className="w-4 h-4 text-primary" />7. Manajemen User & Role</span>
                    </AccordionTrigger>
                    <AccordionContent className="px-2 space-y-2 text-sm text-muted-foreground">
                      <p>Menu <strong>User Management</strong> (hanya tampil untuk Super Admin) menampilkan seluruh akun pengguna beserta jumlah per role.</p>
                      <p>Klik "Ubah Role" untuk mengganti hak akses seorang pengguna menjadi Super Admin, HR, Manager, atau Karyawan.</p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="matrix" data-testid="acc-matrix">
                    <AccordionTrigger className="px-2">
                      <span className="flex items-center gap-2"><Badge variant="secondary" className="text-[10px]">i</Badge>Tabel Hak Akses per Role</span>
                    </AccordionTrigger>
                    <AccordionContent className="px-2">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="role-matrix-table">
                          <thead>
                            <tr className="border-b text-left text-muted-foreground">
                              <th className="py-2 pr-4 font-medium">Menu</th>
                              <th className="py-2 font-medium">Role yang Bisa Akses</th>
                            </tr>
                          </thead>
                          <tbody>
                            {roleMatrix.map((row, idx) => (
                              <tr key={idx} className="border-b last:border-0" data-testid={`role-matrix-row-${idx}`}>
                                <td className="py-2 pr-4">{row.menu}</td>
                                <td className="py-2 text-muted-foreground">{row.roles}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                </Accordion>
              </CardContent>
            </Card>
            <div className="mt-4 flex items-start gap-2 text-xs text-muted-foreground bg-slate-100 rounded-lg p-3">
              <Info className="w-4 h-4 shrink-0 mt-0.5" />
              <p>Modul Payroll, Approval Cuti/Lembur, Performance, Recruitment, dan Dokumen sudah dibangun di sistem namun untuk saat ini belum diaktifkan di menu utama. Bisa diaktifkan kapan saja jika dibutuhkan.</p>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      <footer className="p-4 text-center text-xs text-muted-foreground border-t border-border bg-white mt-8">
        Powered by PT Lintas Sinergi Andalan
      </footer>
    </div>
  );
};

export default PanduanPage;
